import tw from 'twin.macro'
import { useEffect, useRef, useState } from 'react'
import { graphql } from 'gatsby'
import { Popover, PopoverButton, PopoverPanel } from '@headlessui/react'
import { useClickAnyWhere, useEventListener } from 'usehooks-ts'

import { AnimatePresence, motion } from 'framer-motion'
import DynamicAction from '@/components/ui/dynamic-action'
import HeaderCard from '@/components/header-card'
import { buttonStyles } from '@/components/ui/cta-link'
import colors from '@/constants/colors'

export const fragment = graphql`
  fragment HeaderRichMenu on ContentfulHeaderRichMenu {
    __typename
    id
    title
    cards {
      ...HeaderCard
    }
    links {
      ...CtaLink
    }
  }
`

type HeaderRichMenuProps = Queries.HeaderRichMenuFragment & {
  className?: string
  onLinkClick?: () => void
  backgroundColor?: string
}

type HeaderRichMenuContentProps = {
  cards: HeaderRichMenuProps['cards']
  links: HeaderRichMenuProps['links']
  isCondensed: boolean
  onLinkClick?: () => void
}

export function HeaderRichMenuContent({ cards, links, isCondensed, onLinkClick }: HeaderRichMenuContentProps) {
  const cardGroups = cards?.reduce<Array<{ cards: Queries.HeaderCardFragment[]; count: number }>>((acc, card) => {
    if (card.links?.length > 1) {
      acc.push({ cards: [card], count: card.links.length })
    } else {
      const lastGroup = acc[acc.length - 1]
      if (lastGroup && lastGroup.count < 2) {
        lastGroup.cards.push(card)
        lastGroup.count += 1
      } else {
        acc.push({ cards: [card], count: 1 })
      }
    }
    return acc
  }, [])

  return (
    <>
      {cardGroups?.length > 0 && (
        <div
          tw="grid max-w-full gap-y-10 md:gap-y-[45px] md:px-[45px]"
          css={!isCondensed ? tw`grid-flow-col` : tw`grid-cols-2 gap-x-9`}
        >
          {cardGroups.map((group) =>
            group.cards.map((card) => (
              <HeaderCard
                key={card.id}
                css={[
                  tw`grid gap-0`,
                  !isCondensed &&
                    tw`md:(border-r-[1px] border-gray-300 px-10 first-of-type:pl-0 last-of-type:(pr-0 border-r-0))`,
                  group.cards.length > 1
                    ? tw`col-span-2 [&>*]:col-span-2 md:(col-span-1 [&>*]:col-auto!)`
                    : tw`col-span-2`,
                  !isCondensed ? tw`grid-rows-subgrid row-span-3` : tw`grid-cols-subgrid`,
                  group.cards.length === 1 && isCondensed && tw`[&>*]:col-span-2`,
                ]}
                onLinkClick={onLinkClick}
                {...card}
              />
            ))
          )}
        </div>
      )}
      {links?.length > 0 && (
        <div
          css={[
            tw`w-full mt-7 flex flex-col gap-4`,
            tw`md:(flex-row items-center gap-x-6 gap-y-4 px-12)`,
            tw`lg:(py-4 px-10 bg-white flex-row flex-wrap)`,
            isCondensed && links.length > 3 && tw`md:(grid grid-cols-2)`,
          ]}
        >
          {links.map((link) => (
            <DynamicAction
              key={link.id}
              css={[
                tw`no-underline transition-colors text-base`,
                (link.variant === buttonStyles.btnPlain || link.variant === buttonStyles.btnArrow) &&
                  tw`text-primaryBlue! hover:text-indigoBlue! py-0! h-auto!`,
                link.variant === buttonStyles.btnSolid && tw`bg-primaryBlue!`,
              ]}
              onClick={onLinkClick}
              {...(link as any)}
              backgroundColor={colors.white}
            />
          ))}
        </div>
      )}
    </>
  )
}

export default function HeaderRichMenu({
  className,
  title,
  cards,
  links,
  onLinkClick,
  backgroundColor = colors.primaryBlue,
}: HeaderRichMenuProps) {
  const [responsive, setResponsive] = useState({ screenWidth: 0, bottom: 0, left: 0 })
  const buttonRef = useRef<HTMLButtonElement>()

  const onResize = () => {
    if (buttonRef.current) {
      const box = buttonRef.current.getBoundingClientRect()
      setResponsive({ screenWidth: window.innerWidth, bottom: box.bottom, left: box.left })
    }
  }
  useEventListener('resize', onResize)
  useEffect(() => {
    onResize()
  }, [])

  // Hide the outline only when the user clicks on the button
  const [hideOutline, setHideOutline] = useState(false)
  useClickAnyWhere(() => setHideOutline(false))
  useEventListener('keydown', () => {
    setHideOutline(false)
  })

  const isCondensed = responsive.screenWidth < 1314

  return (
    <Popover as="nav" className={className}>
      {({ open }) => (
        <>
          <PopoverButton
            ref={buttonRef}
            tw="flex items-center gap-1 py-3 pr-1"
            css={[
              hideOutline && tw`outline-none`,
              backgroundColor === colors.primaryBlue && tw`hover:text-gray-300`,
              backgroundColor === colors.white && tw`hover:text-indigoBlue`,
              tw`dark:hover:text-tealBlue`,
            ]}
            onClick={() => setHideOutline(true)}
          >
            <span tw="text-base xl:text-lg">{title}</span>
            <span tw="font-bwi text-xs duration-150 rotate-0" css={[open && tw`-rotate-90`]}>
              &#xe92d;
            </span>
          </PopoverButton>

          <div
            tw="absolute left-5 translate-y-1"
            css={[!isCondensed ? tw`w-[calc(100% - 2.5rem)]` : tw`w-[680px]`]}
            style={{
              left: !isCondensed ? '1.25rem' : responsive.left,
            }}
          >
            <AnimatePresence>
              {open && (
                <PopoverPanel
                  tw="bg-gray-100 rounded-2xl transform-gpu overflow-hidden drop-shadow-xl max-w-[1440px] pt-[45px] w-full"
                  key={`header-${title}`}
                  static
                  as={motion.div}
                  initial={{
                    transitionDuration: '20ms',
                    transitionProperty: 'opacity transform',
                    transitionTimingFunction: 'ease-out',
                    opacity: 0.9,
                    scale: 0.95,
                  }}
                  animate={{
                    opacity: 1,
                    scale: 1,
                  }}
                  exit={{}}
                >
                  <HeaderRichMenuContent
                    cards={cards}
                    links={links}
                    isCondensed={isCondensed}
                    onLinkClick={onLinkClick}
                  />
                </PopoverPanel>
              )}
            </AnimatePresence>
          </div>
        </>
      )}
    </Popover>
  )
}
